import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { sendEmail } from '../../api/tagApi';
import { API_BASE_URL } from '../../config/api';
import { getFontById, DEFAULT_FONT } from '../../config/fonts';
import { ACCESSORIES } from '../tag/AccessoryPicker';

const LASER_BACKEND_BASE_URL = API_BASE_URL;

const getAccessoryName = (accessory) =>
  accessory ? ACCESSORIES.find((a) => a.id === accessory.id)?.name || accessory.id : null;

const getFontName = (fontId) => {
  if (!fontId) return DEFAULT_FONT.name;
  return getFontById(fontId)?.name || DEFAULT_FONT.name;
};

// Side text, matching the same "QR Code" vs actual lines logic used
// everywhere else in the flow (TagPreview, Review step).
const getSideLines = (sideNum, orderData) => {
  const isQrSide = orderData?.orderType === 'database' && orderData?.qrCodeSide === sideNum;
  if (isQrSide) {
    const qr = orderData?.qrCodeText;
    const lines = [qr?.line1, qr?.line2, qr?.line3].filter(Boolean);
    return { label: 'QR Code', lines };
  }
  const config = sideNum === 1 ? orderData?.side1Config : orderData?.side2Config;
  const lines = [config?.line1, config?.line2, config?.line3].filter(Boolean);
  return { label: 'Text', lines };
};

function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [confirmedOrderId, setConfirmedOrderId] = useState(null);

  const paymentIntentId = searchParams.get('payment_intent');
  const redirectStatus = searchParams.get('redirect_status');

  useEffect(() => {
    const processOrder = async () => {
      if (!paymentIntentId) {
        setError('No payment intent found in URL');
        setStatus('error');
        return;
      }

      if (redirectStatus !== 'succeeded') {
        setError(`Payment status: ${redirectStatus}`);
        setStatus('error');
        return;
      }

      try {
        // Get payment intent from backend to retrieve orderid and contactid from metadata
        const paymentResponse = await axios.get(
          `${LASER_BACKEND_BASE_URL}/stripePayment/${paymentIntentId}`
        );

        const paymentIntent = paymentResponse.data;
        const orderid = paymentIntent.metadata?.orderid;
        const contactid = paymentIntent.metadata?.contactid;

        if (!orderid || !contactid) {
          throw new Error('Order ID or Contact ID not found in payment intent metadata');
        }

        // Try to get orderData from sessionStorage (stored before payment) —
        // this is the same data used to build the confirmation email, so the
        // page can show the same details instead of a generic message.
        const storedOrderData = sessionStorage.getItem('pendingOrderData');
        let orderData = null;

        if (storedOrderData) {
          try {
            orderData = JSON.parse(storedOrderData);
          } catch (e) {
            console.warn('Failed to parse stored order data:', e);
          }
        }

        // Payment succeeded - send email notification
        console.log('Payment verified:', {
          paymentIntentId: paymentIntentId,
          orderid: orderid,
          contactid: contactid,
          status: paymentIntent.status
        });

        // Send email notification
        // Backend will fetch order/contact details and generate email body
        try {
          // Get email from orderData if available, otherwise backend will fetch from contact
          const recipientEmail = orderData?.notificationEmail || orderData?.contactInfo?.email;

          const emailData = {
            orderid: orderid,
            contactid: contactid,
          };

          // Include email if available, otherwise backend will fetch from contact table
          if (recipientEmail) {
            emailData.email = recipientEmail;
          }

          await sendEmail(emailData);
          console.log('Email sent successfully after payment redirect');
        } catch (emailError) {
          // Log error but don't block success flow
          console.error('Failed to send email after payment redirect:', emailError);
        }

        sessionStorage.removeItem('pendingOrderData');

        setOrderDetails(orderData);
        setConfirmedOrderId(orderid);
        setStatus('success');
      } catch (err) {
        console.error('Error processing order:', err);
        setError(err.response?.data?.error || err.message || 'Failed to process order');
        setStatus('error');
      }
    };

    processOrder();
  }, [paymentIntentId, redirectStatus]);

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg flex items-center justify-center p-4">
        <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-transparent dark:border-dark-border">
          <Loader2 className="w-16 h-16 text-indigo-600 dark:text-dark-primary animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-dark-text mb-2">Processing Your Order</h2>
          <p className="text-gray-600 dark:text-dark-textMuted">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg flex items-center justify-center p-4">
        <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-transparent dark:border-dark-border">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-dark-text mb-2">Order Processing Error</h2>
          <p className="text-gray-600 dark:text-dark-textMuted mb-6">{error}</p>
          <button
            onClick={() => navigate('/create-tag')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
          >
            Return to Order
          </button>
        </div>
      </div>
    );
  }

  // Success — with full details if we have them (sessionStorage can be
  // empty if the customer landed here directly, e.g. a refreshed/bookmarked
  // link), falling back to the plain confirmation otherwise.
  const material = orderDetails?.material;
  const side1 = orderDetails ? getSideLines(1, orderDetails) : null;
  const side2 = orderDetails ? getSideLines(2, orderDetails) : null;
  const side1AccessoryName = getAccessoryName(orderDetails?.side1Accessory);
  const side2AccessoryName = getAccessoryName(orderDetails?.side2Accessory);
  const contact = orderDetails?.contactInfo;
  const fullName = contact
    ? contact.fullname || [contact.firstname, contact.lastname].filter(Boolean).join(' ')
    : null;
  const address = contact
    ? [contact.address_line_1, contact.address_line_2].filter(Boolean).join(', ')
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg flex items-center justify-center p-4 py-10">
      <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl p-8 max-w-xl w-full border border-transparent dark:border-dark-border">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 dark:text-dark-text mb-2">Order Confirmed!</h2>
          <p className="text-gray-600 dark:text-dark-textMuted">
            Thank you for your order. We've received your payment and will process your laser tag shortly.
          </p>
          {confirmedOrderId && (
            <p className="text-sm text-gray-500 dark:text-dark-textMuted mt-2">
              Order #{confirmedOrderId}
            </p>
          )}
        </div>

        {orderDetails && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-border space-y-5">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-dark-textMuted mb-3">
                Tag Details
              </h3>
              <div className="space-y-2 text-sm">
                {material && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500 dark:text-dark-textMuted">Tag Shape:</span>
                    <span className="font-semibold text-gray-800 dark:text-dark-text text-right">
                      {material.shapeName} {material.name ? `- ${material.name}` : ''}
                    </span>
                  </div>
                )}
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500 dark:text-dark-textMuted">Order Type:</span>
                  <span className="font-semibold text-gray-800 dark:text-dark-text text-right">
                    {orderDetails.orderType === 'database' ? 'QR Code Tag' : 'Text-Only Tag'}
                  </span>
                </div>
                {side1 && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500 dark:text-dark-textMuted">Side 1 ({side1.label}):</span>
                    <span className="font-semibold text-gray-800 dark:text-dark-text text-right">
                      {side1.lines.length ? side1.lines.join(' • ') : 'None'}
                    </span>
                  </div>
                )}
                {side2 && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500 dark:text-dark-textMuted">Side 2 ({side2.label}):</span>
                    <span className="font-semibold text-gray-800 dark:text-dark-text text-right">
                      {side2.lines.length ? side2.lines.join(' • ') : 'None'}
                    </span>
                  </div>
                )}
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500 dark:text-dark-textMuted">Font Style:</span>
                  <span className="font-semibold text-gray-800 dark:text-dark-text">
                    {getFontName(orderDetails.selectedFont)}
                  </span>
                </div>
                {(side1AccessoryName || side2AccessoryName) && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500 dark:text-dark-textMuted">Decorations:</span>
                    <span className="font-semibold text-gray-800 dark:text-dark-text text-right">
                      {[
                        side1AccessoryName ? `Side 1: ${side1AccessoryName}` : null,
                        side2AccessoryName ? `Side 2: ${side2AccessoryName}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                )}
                {material?.price != null && (
                  <div className="flex justify-between gap-4 pt-2 border-t border-gray-100 dark:border-dark-border">
                    <span className="text-gray-700 dark:text-dark-text font-semibold">Total:</span>
                    <span className="font-bold text-indigo-600 dark:text-dark-primary">
                      ${material.price}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {(fullName || contact?.phone || address || orderDetails.notificationEmail) && (
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-dark-textMuted mb-3">
                  Shipping To
                </h3>
                <div className="space-y-1 text-sm text-gray-800 dark:text-dark-text">
                  {fullName && <p className="font-semibold">{fullName}</p>}
                  {address && <p>{address}</p>}
                  {contact?.phone && <p>{contact.phone}</p>}
                  {orderDetails.notificationEmail && <p>{orderDetails.notificationEmail}</p>}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="text-center mt-6 pt-6 border-t border-gray-200 dark:border-dark-border">
          <p className="text-sm text-gray-500 dark:text-dark-textMuted mb-6">
            You will also receive an email confirmation shortly with these same order details.
          </p>
          <button
            onClick={() => navigate('/create-tag')}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transform hover:scale-[1.02] transition-all"
          >
            Create Another Tag
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderSuccess;
